import { createSlice } from '@reduxjs/toolkit';
import { isArray } from 'lodash-es';

import { DOCUMENT_SECTION_ORDER } from '@/constants/document';

import { AccountTypeDetails } from '@/types/api/account';
import { PEPQuestion } from '@/types/api/pep';
import {
  Document,
  DocumentSection,
  DocumentType,
  RequiredDocument,
  RequiredDocumentSection,
  SigningDocument,
} from '@/types/document';
import { User } from '@/types/user';

import { compact } from '@/utils/collection';
import {
  findMissingPersonalDetailsDocuments,
  getRequiredDocumentBySection,
  getRequiredDocumentSectionDetails,
  getUserDocumentsBySection,
} from '@/utils/document';

import { signOut } from '../auth/actions';
import { resetOperationData, setOperationData } from './actions';
import {
  notifyMinorDataChanged,
  notifyMinorDataChangedSolved,
} from './actions';

export interface OperationDataState {
  user?: User;
  accountTypes: AccountTypeDetails[];
  shouldRefreshMinorData: boolean;
  accountTypeById: Record<string, AccountTypeDetails>;
  signingDocs: SigningDocument[];
  pepQuestions: PEPQuestion[];
  requiredDocumentSections: RequiredDocumentSection[];
  requiredDocumentBySection: Partial<
    Record<DocumentSection, Partial<Record<DocumentType, RequiredDocument>>>
  >;
  documentsBySection: Record<string, Document[]>;
  documentByDocType: Record<string, Record<string, Document>>;
  initialized: {
    accountTypes: boolean;
    signingDocuments: boolean;
    pepQuestions: boolean;
  };
}

export const INITIAL_OPERATION_DATA_STATE: OperationDataState = {
  accountTypes: [],
  shouldRefreshMinorData: false,
  accountTypeById: {},
  signingDocs: [],
  pepQuestions: [],
  requiredDocumentSections: [],
  requiredDocumentBySection: {},
  documentsBySection: {},
  documentByDocType: {},

  initialized: {
    accountTypes: false,
    signingDocuments: false,
    pepQuestions: false,
  },
};

export const operationDataSlice = createSlice({
  name: 'operation-data',
  initialState: INITIAL_OPERATION_DATA_STATE,
  reducers: {},
  extraReducers(builder) {
    builder.addCase(
      setOperationData,
      (state, { payload: { user, requiredSections, accountTypes } }) => {
        state.user = user;
        state.accountTypes = accountTypes;
        if (state.shouldRefreshMinorData) {
          state.shouldRefreshMinorData = false;
        }
        state.accountTypeById = accountTypes.reduce(
          (acc, type) => ({
            ...acc,
            [type.id]: type,
          }),
          {},
        );

        if (user) {
          const normalizedRequiredSections = requiredSections.map(section => ({
            ...section,
            docs: isArray(section.docs) ? section.docs : [],
          }));

          const { documentByDocType, documentsBySection } =
            getUserDocumentsBySection(user.documents || []);
          state.documentsBySection = documentsBySection;
          state.documentByDocType = documentByDocType;

          // For the moment there will 2 types of requiredDocumentSections
          // 1. Proof of Address (POA)
          // 2. Proof of Employment (POE)
          const requiredDocumentSections = [...normalizedRequiredSections];
          const missingSections = findMissingPersonalDetailsDocuments(
            requiredDocumentSections.map(item => item.type),
          );

          missingSections.forEach(section => {
            requiredDocumentSections.push({
              type: section,
              docs: [],
            });
          });

          requiredDocumentSections.sort(
            (a, b) =>
              (DOCUMENT_SECTION_ORDER[a.type] ?? 0) -
              (DOCUMENT_SECTION_ORDER[b.type] ?? 0),
          );

          const requiredDocumentBySection = getRequiredDocumentBySection(
            requiredDocumentSections,
            user,
          );
          state.requiredDocumentBySection = requiredDocumentBySection;

          const requiredDocSections = requiredDocumentSections.map(item =>
            getRequiredDocumentSectionDetails(
              item,
              requiredDocumentBySection[item.type] || {},
              user,
            ),
          );

          state.requiredDocumentSections = compact(requiredDocSections);
        } else {
          state.documentsBySection = {};
        }
      },
    );
    builder.addCase(notifyMinorDataChanged, state => {
      state.shouldRefreshMinorData = true;
    });
    builder.addCase(notifyMinorDataChangedSolved, state => {
      state.shouldRefreshMinorData = false;
    });
    builder.addCase(resetOperationData, state => {
      Object.assign(state, INITIAL_OPERATION_DATA_STATE);
    });
    builder.addCase(signOut, state => {
      Object.assign(state, {
        ...INITIAL_OPERATION_DATA_STATE,
        user: undefined,
      });
    });
  },
});

export const operationDataReducer = operationDataSlice.reducer;
